import '../../components/mobileStyles.scss'

import { useCallback, useEffect, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router'
import { Drawer, Empty } from 'antd'

import { ChevronRightIcon, CloseIcon } from '@/components/Icons'
import { isValidArray } from '@/libs/utils/array'
import { linkGenerator } from '@/libs/utils/language'

import HeaderLogoSection from '../../components'
import BackButton from './components/BackButton'
import CategoryItem from './components/CategoryItem'
import CategoryTitle from './components/CategoryTitle'

function HeaderSubmenuMobile(props) {
  const {
    isLoading = false,
    isShow = false,
    onClose,
    onFocusSearch,
    btnName: buttonName = '',
    productCategories,
  } = props
  const { t } = useTranslation()
  const navigate = useNavigate()

  // Cat = Categories, Accs = Accessories, Det = Detergents
  const [isShowCatDrawer, setShowCatDrawer] = useState(false)
  const [isShowSubCatDrawer, setShowSubCatDrawer] = useState(false)
  const [isShowAccsDetSubCatDrawer, setShowAccsDetSubCatDrawer] = useState(false)

  const [subCatsToShow, setSubCatsToShow] = useState([])
  const [accsDetSubCatsToShow, setAccsDetSubCatsToShow] = useState([])

  const [parentCat, setParentCat] = useState({})
  const [parentSubCat, setParentSubCat] = useState({})

  const [machines, accessories, detergents] = productCategories?.map(({ categoryId }) => categoryId)
    || []

  const CATEGORY_ID = useMemo(
    () => ({
      machines,
      accessories,
      detergents,
    }),
    [accessories, detergents, machines],
  )

  const onCloseAllDrawers = useCallback(() => {
    setShowCatDrawer(false)
    setShowSubCatDrawer(false)
    setShowAccsDetSubCatDrawer(false)
  }, [])

  useEffect(() => {
    if (!isShow) {
      onCloseAllDrawers()
    }
  }, [isShow, onCloseAllDrawers])

  const onClickSeeCatsOverview = () => {
    navigate(linkGenerator('/categories'))
    onCloseAllDrawers()
    onClose()
  }

  const onCatClick = (id, name, subCats, shouldShowSubCatsDrawer) => {
    if (shouldShowSubCatsDrawer) {
      setSubCatsToShow(subCats)
      setShowSubCatDrawer(true)
      setParentCat({ id, name })
    } else {
      navigate(linkGenerator(`/category/${id}`))
      onCloseAllDrawers()
      onClose()
    }
  }

  const onSubCatClick = (
    id,
    name,
    accsDetSubCats,
    _,
    shouldShowAccsDetSubCatsDrawer,
  ) => {
    if (shouldShowAccsDetSubCatsDrawer) {
      setAccsDetSubCatsToShow(accsDetSubCats)
      setShowAccsDetSubCatDrawer(true)
      setParentSubCat({ id, name })
    } else {
      switch (parentCat?.id) {
        case CATEGORY_ID?.accessories: {
          navigate(
            linkGenerator(
              `/accessories/category/${parentCat?.id}/subcategory/${id}`,
            ),
          )
          break
        }
        case CATEGORY_ID?.detergents: {
          navigate(
            linkGenerator(
              `/detergents/category/${parentCat?.id}/subcategory/${id}`,
            ),
          )
          break
        }
        default: {
          navigate(
            linkGenerator(`/category/${parentCat?.id}/subcategory/${id}`),
          )
        }
      }
      onCloseAllDrawers()
      onClose()
    }
  }

  const onAccsDetSubCatClick = (
    id,
    _name,
    _,
    __,
    ___,
    shouldShowAccsDetSubSubCatsDrawer,
  ) => {
    if (shouldShowAccsDetSubSubCatsDrawer) {
      // TODO: handle showing subCats Drawer of Accs and Det subCats
    } else {
      switch (parentCat?.id) {
        case CATEGORY_ID?.accessories: {
          navigate(
            linkGenerator(
              `/accessories/category/${parentSubCat?.id}/subcategory/${id}`,
            ),
          )
          break
        }
        case CATEGORY_ID?.detergents: {
          navigate(
            linkGenerator(
              `/detergents/category/${parentSubCat?.id}/subcategory/${id}`,
            ),
          )
          break
        }
        default: {
          navigate(linkGenerator(''))
        }
      }
      onCloseAllDrawers()
      onClose()
    }
  }

  const buildDataForAccsAndDetType = useCallback((name, category) => {
    return {
      id: category?.categoryId,
      name,
      subCats: category?.subCategories?.map((item) => ({
        id: item?.categoryId,
        name: item?.categoryName,
        subCats: item?.subCategories?.map((subItem) => ({
          id: subItem?.categoryId,
          name: subItem?.categoryName,
          subCats: subItem?.subCategories,
          shouldShowAccsDetSubSubCatsDrawer: false,
        })),
        shouldShowAccsDetSubCatsDrawer: item?.subCategories && item?.subCategories?.length > 0,
      })),
      shouldShowSubCatsDrawer: category?.subCategories && category?.subCategories?.length > 0,
    }
  }, [])

  const dataToShow = useMemo(() => {
    if (productCategories) {
      let result = []
      if (productCategories[0]) {
        const productCategory = productCategories[0]
        result = productCategory?.subCategories?.map((item) => ({
          id: item?.categoryId,
          name: item?.categoryName,
          subCats: item?.subCategories.map((subItem) => ({
            id: subItem?.categoryId,
            name: subItem?.categoryName,
            subCats: subItem?.subCategories,
            shouldShowAccsDetSubCatsDrawer: false,
          })),
          shouldShowSubCatsDrawer: item?.subCategories && item?.subCategories.length > 0,
        }))
      }
      if (productCategories[1]) {
        const accessoryCategory = productCategories[1]
        result.push(
          buildDataForAccsAndDetType(
            t('searchResultsPage.header.accessories'),
            accessoryCategory,
          ),
        )
      }
      if (productCategories[2]) {
        const detergentsCategory = productCategories[2]
        result.push(
          buildDataForAccsAndDetType(
            t('searchResultsPage.header.detergents'),
            detergentsCategory,
          ),
        )
      }
      return result
    }
    return []
  }, [t, productCategories, buildDataForAccsAndDetType])

  const renderCats = () => {
    if (isLoading) {
      return Array.from(Array.from({ length: 10 }).keys()).map((index) => (
        <CategoryItem key={index} loading id={0} name="" />
      ))
    }
    return dataToShow && dataToShow.length > 0
      ? (
        dataToShow.map((item) => (
          <CategoryItem
            key={item?.id}
            id={item?.id}
            name={item?.name}
            shouldShowSubCatsDrawer={item?.shouldShowSubCatsDrawer}
            subCats={item?.subCats}
            onClick={onCatClick}
          />
        ))
      )
      : <Empty description={t('noData')} />
  }

  const renderSubCats = (subCats) => {
    return isValidArray(subCats)
      ? subCats.map((item) => (
        <CategoryItem
          key={item.id}
          id={item.id}
          name={item.name}
          shouldShowAccsDetSubCatsDrawer={item.shouldShowAccsDetSubCatsDrawer}
          subCats={item.subCats}
          onClick={onSubCatClick}
        />
      ))
      : null
  }

  const renderAccsDetSubCats = (accsDetSubCats) => {
    return isValidArray(accsDetSubCats)
      ? accsDetSubCats.map((item) => (
        <CategoryItem
          key={item.id}
          id={item.id}
          name={item.name}
          subCats={item.subCats}
          shouldShowAccsDetSubSubCatsDrawer={item.shouldShowAccsDetSubSubCatsDrawer}
          onClick={onAccsDetSubCatClick}
        />
      ))
      : null
  }

  const renderAdditionalItem = (name) => {
    return <CategoryItem id={0} name={name} />
  }

  const renderCatsDrawer = () => {
    return (
      <Drawer // Product Categories Drawer (with 2 items Accessories & Detergents at the end)
        closable
        closeIcon={<CloseIcon />}
        open={isShowCatDrawer}
        placement="right"
        rootClassName="header-menu-drawer header-menu-drawer__sub-drawer"
        width={window.innerWidth}
        title={
          <HeaderLogoSection
            inBurgerMenu
            onFocusSearch={onFocusSearch}
            onHideDrawerMobile={onClose}
          />
        }
        onClose={onClose}
      >
        <BackButton
          name={t('header.drawer.button.back')}
          onClick={() => setShowCatDrawer(false)}
        />
        <CategoryTitle
          name={t('header.drawer.title')}
          onClick={onClickSeeCatsOverview}
        />
        {renderCats()}
      </Drawer>
    )
  }

  const renderSubCatsDrawer = () => {
    return (
      <Drawer // Product Sub Categories Drawer (or show Categories of Accessories/Detergents)
        closable
        closeIcon={<CloseIcon />}
        open={isShowSubCatDrawer}
        placement="right"
        rootClassName="header-menu-drawer header-menu-drawer__sub-drawer"
        width={window.innerWidth}
        title={
          <HeaderLogoSection
            inBurgerMenu
            onFocusSearch={onFocusSearch}
            onHideDrawerMobile={onClose}
          />
        }
        onClose={onClose}
      >
        <BackButton
          name={t('header.drawer.button.back')}
          onClick={() => setShowSubCatDrawer(false)}
        />
        <CategoryTitle name={parentCat.name || ''} />
        {renderSubCats(subCatsToShow)}
        {renderAdditionalItem(t('searchResultsPage.header.accessories'))}
        {renderAdditionalItem(t('searchResultsPage.header.detergents'))}
      </Drawer>
    )
  }

  const renderAccsDetSubCatsDrawer = () => {
    return (
      <Drawer // Accessories/Detergents Sub Categories Drawer
        closable
        closeIcon={<CloseIcon />}
        open={isShowAccsDetSubCatDrawer}
        placement="right"
        rootClassName="header-menu-drawer header-menu-drawer__sub-drawer"
        width={window.innerWidth}
        title={
          <HeaderLogoSection
            inBurgerMenu
            onFocusSearch={onFocusSearch}
            onHideDrawerMobile={onClose}
          />
        }
        onClose={onClose}
      >
        <BackButton
          name={t('header.drawer.button.back')}
          onClick={() => setShowAccsDetSubCatDrawer(false)}
        />
        <CategoryTitle name={parentSubCat.name || ''} />
        {renderAccsDetSubCats(accsDetSubCatsToShow)}
        {renderAdditionalItem(t('searchResultsPage.header.accessories'))}
        {renderAdditionalItem(t('searchResultsPage.header.detergents'))}
      </Drawer>
    )
  }

  return (
    <div className="submenu-mobile">
      <button
        className="submenu-mobile__btn"
        type="button"
        onClick={() => setShowCatDrawer(true)}
      >
        {buttonName}
        <ChevronRightIcon />
      </button>
      {renderCatsDrawer()}
      {renderSubCatsDrawer()}
      {renderAccsDetSubCatsDrawer()}
    </div>
  )
}

export default HeaderSubmenuMobile
